import { signOut } from "@bindery/auth-consumer/browser";
import { safeReturnPath } from "@bindery/shared/auth/returnPath";
import { Button } from "@bindery/ui/components/ui/button";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { saveProgressHref } from "@/auth/links";
import {
  markSignOutIntent,
  readerToForget,
  signedInReaderToForget,
  type SignOutIntent,
  signOutIntentOf,
} from "@/auth/sign-out-intent";
import {
  type SessionState,
  type SessionStore,
  sessionStore,
  useSession,
} from "@/convex/session";
import {
  browserResetDeps,
  RESET_STEP_TIMEOUT_MS,
  type ResetProgressDeps,
  within,
} from "@/chrome/reset-progress";

export const SIGN_OUT_PATH = "/sign-out";
export const SIGN_OUT_CONFIRM_PATH = "/sign-out/confirm";
export const SIGNED_OUT_PATH = "/signed-out";

const returnsToSignedOut = (returnTo: string): boolean =>
  new URL(returnTo, location.origin).pathname === SIGNED_OUT_PATH;

/**
 * The app's own sign-out: marks the intent /signed-out acts on, then
 * leaves. A sign-out returning anywhere else clears the mark, so a later
 * visit to /signed-out in this tab cannot act on it.
 */
export function confirmSignOut(
  returnTo: string,
  intent: SignOutIntent | null,
  leave: (returnTo: string) => void = signOut,
): void {
  markSignOutIntent(returnsToSignedOut(returnTo) ? intent : null);
  leave(returnTo);
}

export function signOutReturnTo(raw: string | null): string {
  return raw === null ? SIGNED_OUT_PATH : safeReturnPath(raw);
}

export type SignOutStanding = "saved" | "unsaved" | "unknown";

/** Only an answer that names an account may say progress is saved to it. */
export function signOutStanding(state: SessionState): SignOutStanding {
  if (state.status !== "ready") return "unknown";
  if (state.session.linked === true) return "saved";
  if (state.session.lane === "reader_session" && state.session.linked === false)
    return "unsaved";
  return "unknown";
}

export type SignOutFlushDeps = Pick<
  ResetProgressDeps,
  "flushOutbox" | "hasUnsentRows" | "timeoutMs"
>;

/**
 * Sends what it can, for a bounded time, before a sign-out that takes a
 * reader off this device: anything still unsent then waits for that
 * reader's own session, which only signing in again here brings back. A
 * check that fails or stalls counts as unsent.
 */
export async function unsentAfterFlush(
  intent: SignOutIntent | null,
  {
    flushOutbox,
    hasUnsentRows,
    timeoutMs = RESET_STEP_TIMEOUT_MS,
  }: SignOutFlushDeps,
): Promise<boolean> {
  if (intent === null || intent.readerId === null) return false;
  await within(() => flushOutbox().catch(() => undefined), timeoutMs);
  const readerId = signedInReaderToForget(intent);
  if (readerId === null) return false;
  const unsent = await within(
    () => hasUnsentRows(readerId).catch(() => true),
    timeoutMs,
  );
  return unsent !== false;
}

type SignOutPhase = "asking" | "sending" | "unsent";

// A plain link must not sign anyone out on a shared device: the GET lands
// here, and only the button leaves for /auth/sign-out.
export function SignOutPage({
  signOutTo = confirmSignOut,
  store = sessionStore,
  flushDeps = browserResetDeps,
}: {
  signOutTo?: (returnTo: string, intent: SignOutIntent | null) => void;
  store?: SessionStore;
  flushDeps?: SignOutFlushDeps;
}) {
  const [searchParams] = useSearchParams();
  const returnTo = signOutReturnTo(searchParams.get("returnTo"));
  const session = useSession(store);
  const standing = signOutStanding(session);
  const intent = signOutIntentOf(session);
  const forgetsReader = signedInReaderToForget(intent) !== null;
  const forgetsDeviceReader = !forgetsReader && readerToForget(intent) !== null;
  const offersSaveFirst = forgetsReader && standing !== "saved";
  const [phase, setPhase] = useState<SignOutPhase>("asking");

  const signOutNow = async () => {
    if (phase === "sending") return;
    if (phase === "unsent") {
      signOutTo(returnTo, intent);
      return;
    }
    setPhase("sending");
    if (await unsentAfterFlush(intent, flushDeps)) setPhase("unsent");
    else signOutTo(returnTo, intent);
  };
  const signOutButton = (
    <Button
      type="button"
      size="lg"
      variant={
        phase === "unsent"
          ? "destructive"
          : offersSaveFirst
            ? "outline"
            : "default"
      }
      disabled={phase === "sending"}
      onClick={() => void signOutNow()}
      data-testid="sign-out-cta"
    >
      {phase === "sending"
        ? "Sending answers…"
        : phase === "unsent" || offersSaveFirst
          ? "Sign out anyway"
          : "Sign out"}
    </Button>
  );

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-8 text-center">
      <h1 className="mb-4 text-2xl font-bold">Sign out?</h1>
      {phase === "unsent" && (
        <p role="alert" className="mb-4 max-w-md font-medium">
          Some of this reader&apos;s answers haven&apos;t been sent yet. If you
          sign out now, they&apos;ll be lost unless{" "}
          {standing === "saved"
            ? "this reader signs in again"
            : standing === "unsaved"
              ? "this reader's card is scanned"
              : "this reader's card is scanned, or they sign in if they have an account,"}{" "}
          on this device within 30 days.
        </p>
      )}
      {standing === "unsaved" ? (
        <div className="mb-8 max-w-md space-y-3">
          <p className="font-medium">
            This reader isn&apos;t saved to an account.
          </p>
          <p className="text-muted-foreground">
            If you sign out, this device forgets this reader and their progress.
          </p>
        </div>
      ) : offersSaveFirst ? (
        <div className="mb-8 max-w-md space-y-3">
          <p className="font-medium">
            We can&apos;t tell whether this reader is saved to an account.
          </p>
          <p className="text-muted-foreground">
            If you sign out, this device forgets this reader. Only scanning this
            reader&apos;s card, or signing in if they have an account, brings
            them back.
          </p>
        </div>
      ) : (
        <p className="text-muted-foreground mb-8 max-w-md">
          You&apos;re about to sign out of this device.
          {standing === "saved" &&
            (phase === "unsent"
              ? " Only the answers already sent are saved to your account."
              : " Your reading progress is saved to your account.")}
          {forgetsReader
            ? " This device forgets this reader until they sign in again."
            : forgetsDeviceReader
              ? " This device forgets its reader saved to an account until they sign in again."
              : intent !== null &&
                " Readers who read on this device keep their progress here."}
        </p>
      )}
      <div className="flex flex-col items-center gap-3 sm:flex-row">
        {offersSaveFirst && (
          <Button asChild size="lg" data-testid="save-first-cta">
            <Link to={saveProgressHref("/reader")}>Save this reader first</Link>
          </Button>
        )}
        {signOutButton}
        <Button asChild variant="outline" size="lg">
          <Link to="/">Cancel</Link>
        </Button>
      </div>
    </div>
  );
}
